import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  useReveal, ServiceHero, StatsRow, ProcessSection, WhatWeDeliver,
  ServiceCaseStudies, ServiceFAQ,
  IndustriesSection, WhyZoneX, ServiceCTA, Footer,
} from './shared/ServicePageSections';

const COLOR = '#C0392B';

const PROCESS = [
  {
    num:'01', title:'Requirements & Component Selection',
    desc:'We begin by documenting every hardware requirement precisely — power budget, processing requirements, interface needs, environmental ratings, regulatory targets, and target BOM cost. Component selection happens here, not mid-design, so every part is qualified and available before schematic capture begins.',
    points:[
      'Power budget, voltage rails, and current consumption planning',
      'MCU/SoC, wireless module, and sensor selection and qualification',
      'Component availability, lead time, and alternative sourcing strategy',
      'Regulatory certification pathway identification (CE, FCC, BIS)',
    ],
  },
  {
    num:'02', title:'Schematic Design',
    desc:'Our hardware engineers design detailed schematics covering every subsystem — power supply, MCU circuits, communication interfaces, sensor inputs, and I/O expansion. Every net is named, every decoupling capacitor placed, every power domain isolated. The schematic is the blueprint everything else builds from.',
    points:[
      'Multi-sheet schematic with clear hierarchy and net naming',
      'Power supply design — LDO, buck, boost, battery management',
      'MCU peripheral assignment, clock tree, and memory interface',
      'Communication interface circuits — SPI, I2C, UART, CAN, USB',
    ],
  },
  {
    num:'03', title:'PCB Layout & Routing',
    desc:'We design single, double, or multi-layer PCBs with full attention to signal integrity, EMI control, and thermal management. High-speed signals are length-matched, power planes are properly stitched, and antenna keepouts are respected. DFM constraints from your target manufacturer are applied throughout.',
    points:[
      'Component placement for signal flow, thermal, and assembly efficiency',
      'High-speed and RF routing with impedance control',
      'Power and ground plane management with proper via stitching',
      'DFM review integrated throughout — not bolted on at the end',
    ],
  },
  {
    num:'04', title:'Design Review & Simulation',
    desc:'Before committing to fabrication, we run a structured design review against the requirements specification and perform SPICE simulations to verify power supply stability, signal integrity on critical nets, and filter performance. This is where expensive respins are caught — before they happen.',
    points:[
      'Formal design review against hardware requirements spec',
      'SPICE simulation for power supply and analog signal paths',
      'Signal integrity analysis for high-speed interfaces',
      'Pre-compliance EMC review and antenna return loss check',
    ],
  },
  {
    num:'05', title:'Prototype Fabrication & Bring-Up',
    desc:'We coordinate PCB fabrication and assembly with qualified manufacturers, then bring up prototype boards methodically in our lab — powering rails individually, verifying clocks, testing each peripheral in sequence. When issues arise, we debug them with scopes, logic analysers, and network analysers.',
    points:[
      'Fabrication coordination with qualified PCB and assembly partners',
      'Systematic board bring-up — power sequencing, clock verification, peripheral check',
      'Functional testing and electrical validation against specification',
      'Debug and design correction with full documentation of changes',
    ],
  },
];

const DELIVERABLES = [
  { title:'Complete Schematic Files', desc:'EDA-native schematic files (KiCad/Altium) and PDF exports — fully annotated, hierarchical, and review-ready.' },
  { title:'PCB Layout Files', desc:'Gerber files, ODB++ manufacturing data, Bill of Materials (BOM), and CPL (Component Placement List) for PCB assembly.' },
  { title:'3D PCB Render', desc:'3D PCB render for mechanical fit verification — ensuring your board fits the enclosure before fabrication.' },
  { title:'Component Datasheets & Sourcing', desc:'Curated component datasheet package with preferred suppliers, approved alternatives, and current market lead times.' },
  { title:'Prototype Boards', desc:'Assembled and tested prototype PCBs, brought up in our lab and validated against the hardware requirements specification.' },
  { title:'Hardware Design Documentation', desc:'Complete design documentation including design decisions, test procedures, bring-up guide, and errata for the prototype.' },
  { title:'EMI/EMC Pre-Compliance Report', desc:'Pre-compliance test results and corrective design actions to minimise failure risk at formal certification testing.' },
  { title:'DFM Review Document', desc:'Design-for-manufacture review identifying cost reduction opportunities, assembly risks, and tolerance concerns for production.' },
];

const CASE_STUDIES = [
  {
    tag: 'Utilities',
    title: 'Smart Water Meter PCB',
    metric: '4-layer, CR2032 powered',
    desc: 'Designed a low-power 4-layer PCB for a smart water metering module — custom analog front-end, NB-IoT modem interface, flow pulse counter, and conformal coating for outdoor IP67 deployment.',
  },
  {
    tag: 'Industrial',
    title: 'Ruggedised IoT Gateway',
    metric: '-40°C to +85°C rated',
    desc: 'Custom gateway hardware for an outdoor industrial monitoring application — designed for harsh temperature extremes with proper derating, DIN-rail mounting, RS485/Modbus integration, and cellular connectivity.',
  },
  {
    tag: 'Agriculture',
    title: 'Multi-Sensor Node for Precision Farming',
    metric: '18-month battery life target met',
    desc: 'Compact 6-layer PCB integrating soil NPK, temperature, humidity, and optical rain sensors with an ultra-low-power MCU, LoRa transceiver, and solar energy harvesting for field deployment.',
  },
];

const FAQS = [
  {
    q: 'What EDA tools do you use for schematic and PCB design?',
    a: 'Our hardware engineers work with industry-standard EDA tools including KiCad and Altium Designer. Outputs are delivered in native EDA format along with Gerber, ODB++, BOM, and CPL files ready for any PCB fabricator or contract manufacturer.',
  },
  {
    q: 'Do you handle PCB fabrication and assembly, or just the design?',
    a: 'We coordinate the full fabrication and assembly process with qualified Indian PCB manufacturers and assemblers. We manage the production process and handle board bring-up and testing in our lab — so you receive tested, working prototype boards, not just design files.',
  },
  {
    q: 'Can you design PCBs that meet CE, FCC, or BIS certification requirements?',
    a: 'Yes. We design with regulatory compliance in mind from the first schematic — choosing compliant components, applying correct EMI control techniques, and following antenna design best practices. We provide pre-compliance EMI/EMC review and can support you through formal certification testing with accredited labs.',
  },
  {
    q: 'What is DFM and why does it matter for our product?',
    a: 'DFM stands for Design for Manufacturability — a set of design practices that make a PCB easier, faster, and cheaper to manufacture at scale. Ignoring DFM during design often means expensive rework or redesigns when you move from prototype to production. We apply DFM rules throughout the design process, not as a final checklist.',
  },
  {
    q: 'Can you design for harsh industrial or outdoor environments?',
    a: 'Yes. We have experience designing hardware for industrial environments including factory floors, outdoor enclosures, agricultural deployments, and medical-grade applications — with appropriate component derating, conformal coating, connector selection, and thermal management for the target operating conditions.',
  },
];

function HeroVisual() {
  return (
    <img src="/visuals/services/Service 4.png" alt="Embedded Hardware Design" style={{ width:'100%', display:'block' }} />
  );
}

export default function EmbeddedHardwarePage() {
  const [heroRef, heroVisible] = useReveal();
  return (
    <>
      <Helmet>
        <title>Embedded Hardware Design & PCB Engineering India — Zone-X</title>
        <meta name="description" content="Zone-X provides embedded hardware design and PCB engineering services in India — schematic capture, multi-layer PCB layout, RF design, prototype build, and DFM for production." />
        <link rel="canonical" href="https://zone-x.dev/embedded-hardware-design" />
      </Helmet>

      <div ref={heroRef}>
        <ServiceHero
          tag="Services / Embedded Hardware"
          breadcrumb="Hardware Design"
          title="Embedded Hardware Design &"
          titleAccent="PCB Engineering"
          subtitle="Great IoT products start with great hardware. Precision electronics design, purpose-built for your product — with DFM, signal integrity, and regulatory compliance baked in from the first schematic. Because a hardware problem discovered at certification costs months and money you can't afford."
          geo="Embedded hardware design and PCB engineering services for businesses in Bangalore, Karnataka, and across India."
          color={COLOR}
          visible={heroVisible}
          visual={<HeroVisual />}
        />
      </div>

      <StatsRow />
      <WhyHardwareMatters color={COLOR} />
      <ProcessSection steps={PROCESS} color={COLOR} />
      <WhatWeDeliver items={DELIVERABLES} color={COLOR} />
      <ServiceCaseStudies studies={CASE_STUDIES} color={COLOR} />
      <ServiceFAQ faqs={FAQS} color={COLOR} />
      <IndustriesSection />
      <WhyZoneX />
      <ServiceCTA />
      <Footer />
    </>
  );
}

function WhyHardwareMatters({ color }) {
  const [ref, visible] = useReveal();
  return (
    <section style={{ padding:'0 0 clamp(40px,5vw,64px)' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 clamp(20px,5vw,48px)' }}>
        <div ref={ref} style={{ background:`${color}07`, border:`1px solid ${color}1A`, borderRadius:24, padding:'clamp(28px,4vw,52px)', opacity:visible?1:0, transform:visible?'none':'translateY(22px)', transition:'opacity 520ms, transform 520ms' }}>
          <span style={{ fontFamily:"'Anton', sans-serif", fontSize:10.5, color:color, textTransform:'uppercase' }}>WHY IT MATTERS</span>
          <h2 style={{ fontFamily:"'Anton',sans-serif", fontSize:'clamp(22px,3vw,38px)', lineHeight:1.1, marginTop:8, marginBottom:16, color:'var(--ink)', maxWidth:600 }}>
            Why hardware design can{' '}
            <span style={{ fontFamily:"'Instrument Serif',serif", fontStyle:'italic', fontWeight:400, color:color }}>make or break your IoT product</span>
          </h2>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'clamp(20px,3vw,40px)' }} className="hw-two-col">
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              A hardware problem discovered at the certification stage — or worse, after deployment — is catastrophic. Board respins are expensive, they take months, and every one of them delays your market entry. At Zone-X, we engineer hardware with the end in mind: DFM, EMC compliance, and reliability baked in from the first schematic, not bolted on at the end.
            </p>
            <p style={{ fontSize:15, lineHeight:1.82, color:'var(--ink-soft)' }}>
              Our hardware engineers have designed boards for medical devices, industrial controllers, consumer IoT products, and utility-grade field equipment. We understand the difference between a prototype that works on a bench and a product that survives two years of continuous operation in a factory or a field in the rain.
            </p>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:760px){.hw-two-col{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
}
